import DOMPurify from 'dompurify';
import { liftImgStyleDimensions } from './email-style-utils';

type SanitizeHtmlConfig = {
    ALLOWED_TAGS: string[];
    ALLOWED_ATTR: string[];
    FORBID_TAGS: string[];
    FORBID_ATTR: string[];
    ALLOWED_URI_REGEXP: RegExp;
};

const emailSafeConfig: SanitizeHtmlConfig = {
    ALLOWED_TAGS: [
        'a',
        'b',
        'blockquote',
        'br',
        'div',
        'em',
        // 'font', // Removed: font with color attribute can be used to hide text (e.g. white text on white background)
        'h1',
        'h2',
        'h3',
        'i',
        'img',
        'li',
        'ol',
        'ul',
        'p',
        'span',
        'strike',
        'strong',
        'table',
        'tbody',
        'td',
        'th',
        'thead',
        'tr',
        'u',
    ],
    ALLOWED_ATTR: [
        // 'color', // Removed: only relevant for <font> tag, which is now disallowed
        'dir',
        'href',
        'src',
        'alt',
        'title',
        'width',
        'height',
        'style', // Allowed, but the value is filtered to a safe property allowlist by filterStyle (see the uponSanitizeAttribute hook). Blocks color/font-size/positioning/url() etc.
        // 'class', // Removed: allowing class lets email senders apply application CSS classes, risking UI manipulation
    ],
    FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'button'], // While what is allowed / not allowed is already restricted in ALLOWED_TAGS and ALLOWED_ATTR, we explicitly have a list of forbidden as well, so that these forbidden tags / attributes would have to be explicitly removed from the forbidden list if they were to be used, as to avoid accidental enabling of dangerous tags / attributes
    FORBID_ATTR: ['srcset'], // See comment above. NOTE: 'style' is allowed but sanitized by filterStyle.
    ALLOWED_URI_REGEXP: /^(?!(javascript|vbscript):|(data:(?!image\/)))/i, // Blocks known dangerous URI schemes. Allows data:image/ through so the replace callback can handle it (with SVG exclusion there). Custom filtering in EmailBody's replace callback provides the primary allowlist layer.
};

// Inline-style sanitization (default-deny). Only layout/spacing and basic text
// properties are kept — NOTHING that can recolour, hide, or reposition content.
// This lets senders' own spacing (e.g. margin:0 signatures) survive without
// exposing the vectors that motivated stripping style entirely:
//   • color / background → recolouring, white-on-white invisible text
//   • display / visibility / opacity → hiding content
//   • position / z-index / transform / float / clip / text-indent → escaping
//     the message container, overlaying app chrome, or off-screen hiding
//   • width / height → zero-size hiding or huge blow-ups
//   • url(...) → tracking pixels / outbound requests
// font-size IS allowed, but only in absolute units within a readable range (see
// fontSizeIsSafe) so it can't be shrunk to invisibility (1px text smuggling
// prompt-injection payloads) or compounded via relative units.
const ALLOWED_STYLE_PROPERTIES = new Set([
    'margin',
    'margin-top',
    'margin-right',
    'margin-bottom',
    'margin-left',
    'padding',
    'padding-top',
    'padding-right',
    'padding-bottom',
    'padding-left',
    'text-align',
    'vertical-align',
    'line-height',
    'font-size',
    'font-weight',
    'font-style',
    'text-decoration',
    'text-decoration-line',
    'list-style-type',
    'list-style-position',
    'border',
    'border-width',
    'border-style',
    'border-color',
    'border-collapse',
    'border-top',
    'border-right',
    'border-bottom',
    'border-left',
]);

// Properties where a negative value could be used to hide/displace content.
const NEGATIVE_DISALLOWED_PROPERTIES = new Set([
    'margin',
    'margin-top',
    'margin-right',
    'margin-bottom',
    'margin-left',
    'padding',
    'padding-top',
    'padding-right',
    'padding-bottom',
    'padding-left',
]);

// Tokens that must never appear in a value, regardless of property. calc() is
// blocked outright: no allowlisted property needs it for email layout, and it
// is an easy way to smuggle a negative length past the negative-value check
// below (e.g. `margin: calc(0px-9999px)`).
const UNSAFE_STYLE_VALUE =
    /url\(|expression\(|javascript:|@import|calc\(|[\\<>{}]|\/\*/i;

// A negative sign at the start of the value, after whitespace, or right after
// an opening paren — covers `-5px`, `0 -5px`, and parenthesised forms.
const NEGATIVE_VALUE = /(^|[\s(])-/;

// `vertical-align` accepts arbitrary lengths (incl. negative) that could shift
// content off-screen, so restrict it to the safe keyword set.
const VERTICAL_ALIGN_KEYWORDS = new Set([
    'baseline',
    'sub',
    'super',
    'text-top',
    'text-bottom',
    'middle',
    'top',
    'bottom',
]);

const LINE_HEIGHT_VALUE = /^(\d+(?:\.\d+)?)(px|pt|em|rem|%)?$/i;

// `line-height` could be set to 0 (collapsing/overlapping text) or an extreme
// value; constrain it to sane, positive ranges.
const lineHeightIsSafe = (value: string) => {
    if (value.toLowerCase() === 'normal') return true;
    const match = LINE_HEIGHT_VALUE.exec(value);
    if (!match) return false;
    const amount = parseFloat(match[1]);
    const unit = match[2]?.toLowerCase();
    if (!unit) return amount >= 0.5 && amount <= 3; // unitless multiplier
    if (unit === '%') return amount >= 50 && amount <= 300;
    if (unit === 'em' || unit === 'rem') return amount > 0 && amount <= 4;
    return amount > 0 && amount <= 48; // px / pt
};

const FONT_SIZE_VALUE = /^(\d+(?:\.\d+)?)(px|pt)$/i;

// `font-size` is restricted to absolute units within a readable range. Relative
// units (%, em, rem) and keywords are rejected because they cascade/compound and
// could be driven below the floor (nested 50% → invisible); a floor is the whole
// point, so anything we can't bound is dropped (it falls back to the body size).
const fontSizeIsSafe = (value: string) => {
    const match = FONT_SIZE_VALUE.exec(value);
    if (!match) return false;
    const amount = parseFloat(match[1]);
    const unit = match[2].toLowerCase();
    // 8pt ≈ 10.6px, so both floors land around the same readable size.
    return unit === 'pt'
        ? amount >= 8 && amount <= 36
        : amount >= 10 && amount <= 48;
};

// Per-property value validation for properties that, despite being layout-only,
// accept values that could hide or displace content.
const propertyValueIsSafe = (property: string, value: string) => {
    if (property === 'vertical-align') {
        return VERTICAL_ALIGN_KEYWORDS.has(value.toLowerCase());
    }
    if (property === 'line-height') {
        return lineHeightIsSafe(value);
    }
    if (property === 'font-size') {
        return fontSizeIsSafe(value);
    }
    return true;
};

// Filters an inline style string down to the allowlisted properties with
// validated values, rebuilding the declaration from scratch (so the browser
// only ever sees declarations we explicitly emitted). Returns '' if nothing
// safe remains.
export const filterStyle = (style?: string): string => {
    if (!style) return '';

    const safeDeclarations: string[] = [];
    for (const declaration of style.split(';')) {
        const colonIndex = declaration.indexOf(':');
        if (colonIndex === -1) continue;

        const property = declaration.slice(0, colonIndex).trim().toLowerCase();
        if (!ALLOWED_STYLE_PROPERTIES.has(property)) continue;

        const value = declaration
            .slice(colonIndex + 1)
            .replace(/!\s*important/gi, '')
            .trim();
        if (!value || UNSAFE_STYLE_VALUE.test(value)) continue;
        if (
            NEGATIVE_DISALLOWED_PROPERTIES.has(property) &&
            NEGATIVE_VALUE.test(value)
        ) {
            continue;
        }
        if (!propertyValueIsSafe(property, value)) continue;

        safeDeclarations.push(`${property}: ${value}`);
    }

    return safeDeclarations.join('; ');
};

export const getSanitizedHtml = (contents?: string) => {
    // Two scoped hooks (registered per call, removed afterwards):
    // • uponSanitizeElement: lift img width/height out of `style` onto the
    //   width/height attributes before the style value is filtered.
    // • uponSanitizeAttribute: filter every `style` value down to the safe
    //   property allowlist; drop the attribute entirely if nothing remains.
    try {
        DOMPurify.addHook('uponSanitizeElement', liftImgStyleDimensions);
        DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
            if (data.attrName !== 'style') return;
            data.attrValue = filterStyle(data.attrValue);
            if (!data.attrValue) data.keepAttr = false;
        });
        return DOMPurify.sanitize(contents, emailSafeConfig);
    } catch (error) {
        console.error('Error sanitizing HTML:', error);
        return '';
    } finally {
        DOMPurify.removeHook('uponSanitizeElement');
        DOMPurify.removeHook('uponSanitizeAttribute');
    }
};

// Plain text emails use newlines (\r\n, \n, or \r) for line breaks, but whitespace-normal CSS collapses them.
// If the content has no block-level HTML tags (which handle line breaking in HTML emails),
// we treat it as plain text and convert newlines to <br> so line breaks render correctly.
const BLOCK_LEVEL_HTML_PATTERN =
    /<(br|p|div|h[1-6]|ul|ol|li|blockquote|table|tbody|thead|tr|td|th)\b/i;

export const convertPlainTextNewlines = (content?: string) => {
    if (!content || BLOCK_LEVEL_HTML_PATTERN.test(content)) {
        return content;
    }
    return content.replace(/\r?\n|\r/g, '<br>');
};
